// WAAP Advanced Protection → Weak Password Validation: the account-level
// weak-password dictionary (a newline-separated list of common weak passwords).
// Backed by the PRIVATE portal /weak_password via eagle_waf/advanced. Account-level:
// the `:id` domain only resolves the customer session, so the hooks take an ANCHOR
// domain UUID.
//
//   GET /waap/domains/:id/weak-password   → portal GET /weak_password
//   PUT /waap/domains/:id/weak-password   → portal PUT /weak_password (save, inferred)

import {
  useMutation,
  useQuery,
  useQueryClient,
  type UseMutationOptions,
} from '@tanstack/react-query';
import { serviceApiV3 } from '@/api/client';

/** The weak-password dictionary: the uploaded file metadata + its content. */
export interface WeakPassword {
  /** Newline-separated weak passwords. */
  content: string;
  file?: unknown;
}

function path(domainId: string): string {
  return `/waap/domains/${domainId}/weak-password`;
}

export const weakPasswordKeys = {
  all: ['waap', 'weak-password'] as const,
  get: (domainId: string) => ['waap', 'weak-password', domainId] as const,
};

/** Fetch the weak-password dictionary. Pass `enabled` to defer until "Show List". */
export function useWeakPassword(domainId: string | undefined, enabled: boolean) {
  return useQuery({
    queryKey: weakPasswordKeys.get(domainId ?? ''),
    enabled: !!domainId && enabled,
    retry: false,
    queryFn: async (): Promise<WeakPassword> => {
      const res = await serviceApiV3.get<WeakPassword | { data: WeakPassword }>(
        path(domainId as string),
      );
      const body = res.data as WeakPassword | { data?: WeakPassword };
      return (body as { data?: WeakPassword })?.data ?? (body as WeakPassword);
    },
  });
}

/** Save the edited weak-password dictionary. */
export function useUpdateWeakPassword(
  domainId: string,
  options?: UseMutationOptions<unknown, unknown, string>,
) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (content: string) => {
      const res = await serviceApiV3.put(path(domainId), { content });
      return res.data;
    },
    ...options,
    onSuccess: (...args) => {
      qc.invalidateQueries({ queryKey: weakPasswordKeys.get(domainId) });
      options?.onSuccess?.(...args);
    },
  });
}

// ---- Service-scoped twins (account-level, work with zero websites) ----------

function servicePath(serviceId: string): string {
  return `/services/${serviceId}/waap/weak-password`;
}

export const weakPasswordServiceKeys = {
  get: (serviceId: string) => ['waap', 'weak-password', 'service', serviceId] as const,
};

/** Fetch the weak-password dictionary, service-scoped. */
export function useServiceWeakPassword(serviceId: string | undefined, enabled: boolean) {
  return useQuery({
    queryKey: weakPasswordServiceKeys.get(serviceId ?? ''),
    enabled: !!serviceId && enabled,
    retry: false,
    queryFn: async (): Promise<WeakPassword> => {
      const res = await serviceApiV3.get<WeakPassword | { data: WeakPassword }>(
        servicePath(serviceId as string),
      );
      const body = res.data as WeakPassword | { data?: WeakPassword };
      return (body as { data?: WeakPassword })?.data ?? (body as WeakPassword);
    },
  });
}

/** Save the edited weak-password dictionary, service-scoped. */
export function useUpdateServiceWeakPassword(
  serviceId: string,
  options?: UseMutationOptions<unknown, unknown, string>,
) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (content: string) => {
      const res = await serviceApiV3.put(servicePath(serviceId), { content });
      return res.data;
    },
    ...options,
    onSuccess: (...args) => {
      qc.invalidateQueries({ queryKey: weakPasswordServiceKeys.get(serviceId) });
      options?.onSuccess?.(...args);
    },
  });
}
